

import { buildPeriodSlots } from "../Triggers/hooks/useTriggers";

/** The scope every location inherits from — one cadence for the whole company. */
export const COMPANY_WIDE_SCOPE = { locationId: 0, subLocationId: 0 };

/**
 * Sentinel a row carries when the locations being edited disagree on its cadence.
 *
 * Never sent to the API: it describes what several sites currently hold, not a
 * cadence anything can be set to.
 */
export const MIXED_VALUE = "__MIXED__";

/**
 * The cadences a KPI can report at.
 *
 * `monthSpan` is how many months one period covers; CUSTOM has none because its
 * periods are not derivable — the data owner types a from/to range at
 * data-entry time instead.
 */
export const FREQUENCY_OPTIONS = [
  { value: "MONTHLY", label: "Monthly", monthSpan: 1 },
  { value: "QUARTERLY", label: "Quarterly", monthSpan: 3 },
  { value: "HALF_YEARLY", label: "Half Yearly", monthSpan: 6 },
  { value: "YEARLY", label: "Yearly", monthSpan: 12 },
  { value: "CUSTOM", label: "Custom", monthSpan: null },
];

/**
 * Choices for the frequency filter: every cadence a row can read.
 *
 * With several locations being edited a "Mixed" choice is added, which is the
 * only way to find the KPIs those locations disagree on.
 * @param {boolean} [includeMixed] true when more than one location is being edited.
 * @returns {Array<{value: string, label: string}>} the filter choices.
 */
export function frequencyFilterOptions(includeMixed = false) {
  return [
    ...FREQUENCY_OPTIONS.map(({ value, label }) => ({ value, label })),
    ...(includeMixed ? [{ value: MIXED_VALUE, label: "Mixed across locations" }] : []),
  ];
}

/** Cadence definitions keyed by value, so a lookup never scans the option list. */
const FREQUENCY_BY_VALUE = FREQUENCY_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option;
  return acc;
}, {});

/** Short month names used to label derived reporting periods. */
const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * Parses JSON that may be absent or corrupt, without throwing.
 * @param {string|null} raw the stored string.
 * @param {*} fallback returned when parsing fails or yields null.
 * @returns {*} the parsed value or the fallback.
 */
export function safeParseJSON(raw, fallback = null) {
  if (raw == null) return fallback;
  try {
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

/**
 * The sites a cadence can be set for, as dropdown options.
 *
 * Company-wide is deliberately absent: it is not a site alongside the others but
 * what an empty selection means, so offering it as a checkbox would let it be
 * ticked together with locations it actually sits above. For the same reason a
 * site split into sub-locations is represented only by those sub-locations —
 * data is reported against them, so offering the parent as well would let one
 * selection write two tiers that then disagree about which cadence applies. A
 * site with no sub-locations is itself where data lands, so it is offered.
 * @param {object[]} sources raw `getSource` rows.
 * @returns {Array<{value: string, label: string, locationId: number, subLocationId: number}>} the options.
 */
export function locationOptions(sources) {
  const options = [];

  (Array.isArray(sources) ? sources : []).forEach((source) => {
    const locationId = Number(source?.id);
    if (!locationId) return;
    const name = source.unitCode || source.location?.area || `Location ${locationId}`;
    const subLocations = (source.subLocation || []).filter((sub) => Number(sub?.id) > 0);

    if (!subLocations.length) {
      options.push({ value: String(locationId), label: name, locationId, subLocationId: 0 });
      return;
    }

    subLocations.forEach((sub) => {
      const subLocationId = Number(sub.id);
      options.push({
        value: `${locationId}_${subLocationId}`,
        label: `${name} › ${sub.subLocation}`,
        locationId,
        subLocationId,
      });
    });
  });

  return options;
}

/** How the company-wide scope keys itself, since no dropdown option names it. */
const COMPANY_WIDE_KEY = "all";

/**
 * Resolves a scope dropdown value back into the scope it names.
 *
 * Unknown and malformed values fall back to company-wide rather than throwing: a
 * stale selection should widen the view, never break the page.
 *
 * That widening makes this a read-path resolver only. Write paths must take
 * their scopes from {@link scopesFromValues}, which drops company-wide scopes,
 * so a value that named nothing recognisable ends up writing nowhere instead of
 * writing to the row every site inherits.
 * @param {string} value a {@link locationOptions} value.
 * @returns {{locationId: number, subLocationId: number}} the scope.
 */
export function parseScopeValue(value) {
  const [locationPart, subLocationPart] = String(value ?? "").split("_");
  const locationId = Number(locationPart) || 0;
  if (!locationId) return { ...COMPANY_WIDE_SCOPE };
  return { locationId, subLocationId: Number(subLocationPart) || 0 };
}

/**
 * The dropdown value that stands for a scope — the inverse of {@link parseScopeValue}.
 * @param {{locationId: number, subLocationId: number}} scope the scope.
 * @returns {string} the option value, or `all` for the company-wide scope.
 */
export function scopeValue(scope) {
  const locationId = Number(scope?.locationId) || 0;
  if (!locationId) return COMPANY_WIDE_KEY;
  const subLocationId = Number(scope?.subLocationId) || 0;
  return subLocationId ? `${locationId}_${subLocationId}` : String(locationId);
}

/**
 * Whether a scope is the company-wide one.
 * @param {{locationId: number, subLocationId: number}} scope the scope.
 * @returns {boolean} true when no location narrows it.
 */
export function isCompanyWide(scope) {
  return !(Number(scope?.locationId) || 0);
}

/**
 * The scopes a dropdown selection names — the whole selection, not one site.
 *
 * An empty selection is empty: cadences are set at locations, so with none
 * picked there is nothing to edit and the page says so rather than quietly
 * writing somewhere the person did not choose. Repeats collapse so a selection
 * can never write the same site twice.
 *
 * This is the gate every write goes through, and the reason company-wide scopes
 * are dropped rather than kept: {@link parseScopeValue} widens anything it does
 * not recognise to company-wide, and a save must not turn a stale or malformed
 * selection into an edit of the row every site inherits.
 * @param {string[]} values {@link locationOptions} values that are selected.
 * @returns {Array<{locationId: number, subLocationId: number}>} the scopes.
 */
export function scopesFromValues(values) {
  const scopes = [];
  const seen = new Set();

  (Array.isArray(values) ? values : []).forEach((value) => {
    const scope = parseScopeValue(value);
    if (isCompanyWide(scope)) return;
    const key = scopeValue(scope);
    if (seen.has(key)) return;
    seen.add(key);
    scopes.push(scope);
  });

  return scopes;
}

/**
 * A selection's identity as a string, for effect and memo dependencies.
 *
 * Scope objects are rebuilt on every render, so depending on the array itself
 * would refetch the page endlessly; this collapses it to a value that only
 * changes when the sites do.
 * @param {Array<{locationId: number, subLocationId: number}>} scopes the selection.
 * @returns {string} a stable key.
 */
export function scopesKey(scopes) {
  return (scopes || []).map(scopeValue).join("|");
}

/**
 * The scopes a {@link scopesKey} names — its inverse.
 *
 * Lets a memo depend on the key while still producing the array, without reading
 * anything the dependency list does not cover: the scopes are rebuilt out of the
 * key itself, so there is no stale value to suppress a lint warning over. The
 * round trip is exact because every scope in a selection came from
 * {@link scopesFromValues}, which is also what reads the key back.
 * @param {string} key a key from {@link scopesKey}.
 * @returns {Array<{locationId: number, subLocationId: number}>} the scopes it names, in order.
 */
export function scopesFromKey(key) {
  return scopesFromValues(String(key ?? "").split("|"));
}

/**
 * Names the sites being edited, in the words a message can use.
 *
 * Beyond two the names are dropped for a count: a message that lists fifteen
 * sites is no longer read, and the dropdown already shows which they are.
 * @param {string[]} labels the selected sites' labels.
 * @returns {string} e.g. `Chennai`, `Chennai and Mumbai`, `4 locations`.
 */
export function describeLocations(labels) {
  const named = (labels || []).filter(Boolean);
  if (!named.length) return "no locations";
  if (named.length === 1) return named[0];
  if (named.length === 2) return `${named[0]} and ${named[1]}`;
  return `${named.length} locations`;
}

/**
 * What one KPI reports at, site by site — the answer behind a "Mixed" row.
 *
 * A site that configures nothing is reported at the cadence it inherits rather
 * than left blank, because that inherited cadence is what its reporters actually
 * see; `isInherited` is what separates the two.
 * @param {number} kpiId the KPI.
 * @param {Array<{key: string, label: string, map: object}>} sites the selected sites and their saved cadences.
 * @param {string} baseline the cadence a site with no setting of its own falls back to.
 * @returns {Array<{key: string, label: string, frequency: string, isInherited: boolean}>} one entry per site.
 */
export function locationBreakdown(kpiId, sites, baseline) {
  return (Array.isArray(sites) ? sites : []).map((site) => {
    const frequency = site.map?.[kpiId];
    return {
      key: site.key,
      label: site.label,
      frequency: frequency || baseline,
      isInherited: !frequency,
    };
  });
}

/**
 * Folds each selected site's saved cadences into the one map the rows render from.
 *
 * A KPI the selected sites disagree on — different cadences, or set at some and
 * inherited at others — collapses to {@link MIXED_VALUE} rather than to one
 * site's value, so the screen can never show a cadence that only part of the
 * selection actually has.
 * @param {object[]} maps one `{ [kpiId]: frequency }` per selected site.
 * @returns {object} the merged map, keyed by KPI id.
 */
export function mergeSavedMaps(maps) {
  const scopeMaps = (Array.isArray(maps) ? maps : []).map((map) => map || {});
  if (scopeMaps.length <= 1) return { ...(scopeMaps[0] || {}) };

  const merged = {};
  const kpiIds = new Set(scopeMaps.flatMap((map) => Object.keys(map)));

  for (const kpiId of kpiIds) {
    const first = scopeMaps[0][kpiId];
    merged[kpiId] = scopeMaps.every((map) => map[kpiId] === first) ? first : MIXED_VALUE;
  }
  return merged;
}

/**
 * The cadence a KPI falls back to when it sets none at the current scope.
 *
 * At a location or sub-location that is the company-wide setting if one exists,
 * and the financial-year default otherwise — so a row's "Inherit" option names
 * what clearing it would actually produce, not a default the company has already
 * overridden. Pass an empty map at the company-wide scope, where a company row is
 * the override rather than something to inherit.
 * @param {number} kpiId the KPI.
 * @param {object} baselineMap company-wide overrides keyed by KPI id.
 * @param {string} defaultFrequency the financial-year default.
 * @returns {string} the cadence inherited in the absence of an override.
 */
export function baselineFrequency(kpiId, baselineMap, defaultFrequency) {
  return baselineMap?.[kpiId] || defaultFrequency;
}

/**
 * Human label for a frequency value.
 * @param {string} value a FREQUENCY_OPTIONS value.
 * @returns {string} the label, or the raw value when unrecognised.
 */
export function frequencyLabel(value) {
  return FREQUENCY_BY_VALUE[value]?.label || value || "—";
}

/**
 * The frequency a KPI reports at right now, honouring unsaved edits.
 *
 * Single source of truth for the fallback order — staged edit, then saved
 * override, then the financial-year default — so the row select, the period
 * preview, the filter and the dirty check can never disagree. Every row
 * therefore shows a real cadence rather than a "same as the default" choice
 * standing in for one. Returns {@link MIXED_VALUE} when the selected sites hold
 * different cadences and nothing is staged over them: there is no single
 * frequency to report.
 * @param {number} kpiId the KPI.
 * @param {object} pending staged edits keyed by KPI id.
 * @param {object} savedMap saved overrides keyed by KPI id.
 * @param {string} defaultFrequency the financial-year default.
 * @returns {string} the effective frequency value.
 */
export function resolveFrequency(kpiId, pending, savedMap, defaultFrequency) {
  return pending[kpiId] || savedMap[kpiId] || defaultFrequency;
}

/**
 * Whether a KPI carries a cadence of its own rather than taking the default.
 * @param {number} kpiId the KPI.
 * @param {object} pending staged edits keyed by KPI id.
 * @param {object} savedMap saved overrides keyed by KPI id.
 * @returns {boolean} true when the KPI has its own frequency.
 */
export function hasOverride(kpiId, pending, savedMap) {
  return Boolean(pending[kpiId] || savedMap[kpiId]);
}

/**
 * Whether a KPI's frequency is one the filter is showing.
 *
 * Matches on the cadence the row displays, so what the filter selects is what
 * the list shows.
 * @param {number} kpiId the KPI.
 * @param {object} pending staged edits keyed by KPI id.
 * @param {object} savedMap saved overrides keyed by KPI id.
 * @param {string[]} selectedValues the active filter values; empty means no filter.
 * @param {string} [defaultFrequency] the financial-year default.
 * @returns {boolean} true when the KPI should be shown.
 */
export function matchesFrequencyFilter(kpiId, pending, savedMap, selectedValues, defaultFrequency) {
  if (!selectedValues?.length) return true;
  return selectedValues.includes(resolveFrequency(kpiId, pending, savedMap, defaultFrequency));
}

/**
 * Stages a frequency for many KPIs at once — the Bulk Apply action.
 *
 * Choosing the cadence a row already reports at is dropped rather than staged,
 * so applying the current value to a selection never produces a phantom change
 * — and that holds for a row taking the financial-year default, which reads as
 * that cadence rather than as a choice of its own. A KPI the selected sites
 * disagree on always stages: whatever is chosen, some of them are about to change.
 * @param {object} pending the current staged edits.
 * @param {number[]} kpiIds the KPIs to apply to.
 * @param {string} value a FREQUENCY_OPTIONS value.
 * @param {object} savedMap saved overrides keyed by KPI id.
 * @param {Function} [baselineOf] the default a KPI reports at when it sets nothing.
 * @returns {object} the next staged-edit map.
 */
export function stageFrequency(pending, kpiIds, value, savedMap, baselineOf) {
  const next = { ...pending };
  for (const kpiId of kpiIds) {
    const current = savedMap[kpiId] ?? baselineOf?.(kpiId);
    if (current === value) delete next[kpiId];
    else next[kpiId] = value;
  }
  return next;
}

/**
 * Turns staged edits into the bulk endpoint's payload.
 * @param {object} pending staged edits keyed by KPI id.
 * @returns {Array<{kpiId: number, frequency: string}>} the payload entries.
 */
export function buildFrequencyPayload(pending) {
  return Object.keys(pending).map((kpiId) => ({
    kpiId: Number(kpiId),
    frequency: pending[kpiId],
  }));
}

/**
 * First calendar year of a financial-year label such as "2024-25" or "2024-2025".
 * @param {string} fyValue the financial year label.
 * @returns {number|null} the starting year, or null when unparseable.
 */
export function fyStartYear(fyValue) {
  const match = String(fyValue || "").match(/\d{4}/);
  return match ? Number(match[0]) : null;
}

/**
 * The fiscal start month to anchor periods at, defaulting to April when the
 * company's setting is missing or out of range.
 * @param {number} startMonth 1-based fiscal start month.
 * @returns {number} a month between 1 and 12.
 */
function fiscalAnchor(startMonth) {
  const month = Number(startMonth);
  return month >= 1 && month <= 12 ? month : 4;
}

/**
 * Where a financial year sits relative to today: "past", "current" or "future".
 *
 * Decided by the fiscal calendar rather than by which entry is newest, so a year
 * the company has created ahead of time reads as "future" — still configurable,
 * since setting next year's cadences before it starts is the point of doing it
 * ahead — and only a year that has fully ended reads as "past" and locks.
 * A label with no parseable year is treated as "current" rather than locked:
 * refusing edits on an unreadable label would be a silent dead end.
 * @param {string} fyValue financial-year label such as "2026-2027".
 * @param {number} startMonth 1-based fiscal start month.
 * @param {Date} [today] the moment to judge against.
 * @returns {"past"|"current"|"future"} the year's standing.
 */
export function fyStatus(fyValue, startMonth, today = new Date()) {
  const startYear = fyStartYear(fyValue);
  if (startYear === null) return "current";

  const firstMonth = startYear * 12 + (fiscalAnchor(startMonth) - 1);
  const thisMonth = today.getFullYear() * 12 + today.getMonth();

  if (thisMonth < firstMonth) return "future";
  return thisMonth < firstMonth + 12 ? "current" : "past";
}

/**
 * The reporting periods a frequency produces for one financial year.
 *
 * Boundaries come from `buildPeriodSlots` — the same generator the Triggers page
 * offers as its Period filter and Add Data prefills a range from — so the
 * preview here can never disagree with the periods a KPI is actually reported
 * against. Only the labels are local: the preview shows compact month chips
 * ("Apr 2026", "Apr 2026 – Sep 2026") where those screens name slots Q1/H1.
 *
 * Periods are anchored at `startMonth`, the head office's fiscal start, so a
 * half-yearly KPI on an April start reads Apr–Sep, Oct–Mar rather than the
 * calendar halves. CUSTOM yields no periods: its range is chosen per submission,
 * and neither does a financial year whose label carries no year to anchor to.
 * @param {string} frequency a FREQUENCY_OPTIONS value.
 * @param {number} startMonth 1-based fiscal start month.
 * @param {string} [fyValue] financial-year label such as "2026-2027".
 * @returns {Array<{key: string, label: string}>} the ordered periods.
 */
export function derivePeriods(frequency, startMonth, fyValue) {
  if (!FREQUENCY_BY_VALUE[frequency]?.monthSpan) return [];

  const startYear = fyStartYear(fyValue);
  if (startYear === null) return [];

  const slots =
    buildPeriodSlots(`${startYear}-${startYear + 1}`, fiscalAnchor(startMonth))[frequency] || [];

  return slots.map((slot) => ({
    key: slot.fromDate,
    label: periodChipLabel(slot.fromDate, slot.toDate),
  }));
}

/**
 * Renders a slot's boundaries as "Apr 2026", or "Apr 2026 – Sep 2026" when it
 * spans more than one month.
 * @param {string} fromDate `YYYY-MM-DD` start of the period.
 * @param {string} toDate `YYYY-MM-DD` end of the period.
 * @returns {string} the chip label.
 */
function periodChipLabel(fromDate, toDate) {
  const from = monthStamp(fromDate);
  const to = monthStamp(toDate);
  return from === to ? from : `${from} – ${to}`;
}

/**
 * Renders the month an ISO date falls in as "Apr 2026".
 * @param {string} isoDate a `YYYY-MM-DD` date.
 * @returns {string} the month label.
 */
function monthStamp(isoDate) {
  const [year, month] = String(isoDate).split("-");
  return `${MONTH_NAMES[Number(month) - 1]} ${year}`;
}
